import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { ActionButtons } from '@/components';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { AdminTagChipPicker } from '../AdminTagChipPicker';
import type { CorkBoardTag, CorkBoardThreadSummary } from '@/types/residents/corkBoard';

interface DesktopEditThreadTagsPanelProps {
  visible: boolean;
  thread: CorkBoardThreadSummary | null;
  availableTags: CorkBoardTag[];
  isSaving: boolean;
  onSave: (threadId: string, tagIds: number[]) => void;
  onClose: () => void;
}

export const DesktopEditThreadTagsPanel: React.FC<DesktopEditThreadTagsPanelProps> = ({
  visible,
  thread,
  availableTags,
  isSaving,
  onSave,
  onClose,
}) => {
  const { redesign } = useAdminColors();
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  const isAnnouncement = thread?.isAnnouncement ?? false;
  const isSurvey = thread?.kind === 'survey';

  const categoryTags = useMemo(
    () => availableTags.filter((t) => t.slug !== 'announcements' && t.slug !== 'survey'),
    [availableTags],
  );

  const announcementsTagId = useMemo(
    () => availableTags.find((t) => t.slug === 'announcements')?.id ?? null,
    [availableTags],
  );

  const surveyTag = useMemo(
    () => availableTags.find((t) => t.slug === 'survey') ?? null,
    [availableTags],
  );
  const surveyTagId = surveyTag?.id ?? null;

  useEffect(() => {
    if (visible && thread) {
      const tagIds = thread.tags.map((t) => Number(t.id));
      // Strip system tags (announcements / survey) — shown as locked, re-applied on save.
      setSelectedTagIds(tagIds.filter((id) => id !== announcementsTagId && id !== surveyTagId));
    }
  }, [visible, thread, announcementsTagId, surveyTagId]);

  const handleToggleTag = useCallback((tagId: number) => {
    setSelectedTagIds((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      }
      return [tagId];
    });
  }, []);

  const handleSave = useCallback(() => {
    if (isSaving || !thread) return;
    if (!isAnnouncement && !isSurvey && selectedTagIds.length === 0) return;
    const finalTagIds = [...selectedTagIds];
    if (isAnnouncement && announcementsTagId && !finalTagIds.includes(announcementsTagId)) {
      finalTagIds.push(announcementsTagId);
    }
    if (isSurvey && surveyTagId && !finalTagIds.includes(surveyTagId)) {
      finalTagIds.push(surveyTagId);
    }
    // Never submit an empty tag set (the API requires at least one tag).
    if (finalTagIds.length === 0) return;
    onSave(thread.id, finalTagIds);
  }, [isSaving, thread, isAnnouncement, isSurvey, selectedTagIds, announcementsTagId, surveyTagId, onSave]);

  return (
    <DesktopSidePanel visible={visible} onClose={onClose} width={480}>
      <View style={styles.container}>
        <DesktopPanelHeader title={isSurvey ? 'Edit Survey Tags' : 'Edit Thread Tags'} onClose={onClose} />
        <DesktopPanelBody scroll bottomInset={75}>
          <View style={styles.bodyContent}>
            {thread && (
              <Text
                style={[styles.threadTitle, { color: redesign.text.primary }]}
                numberOfLines={1}
              >
                {thread.title}
              </Text>
            )}
            {isAnnouncement && (
              <Text style={[styles.autoTagNote, { color: redesign.text.subtle }]}>
                Announcements tag is applied automatically
              </Text>
            )}
            {isSurvey && (
              <Text style={[styles.autoTagNote, { color: redesign.text.subtle }]}>
                Survey tag is applied automatically
              </Text>
            )}
            <AdminTagChipPicker
              tags={categoryTags}
              selectedTagIds={selectedTagIds}
              onToggleTag={handleToggleTag}
              lockedTag={isSurvey ? surveyTag : null}
              label={isAnnouncement || isSurvey ? 'Category tag (optional)' : 'Select a tag'}
            />
          </View>
        </DesktopPanelBody>
        <View style={[styles.footer, { borderTopColor: redesign.card.border }]}>
          <ActionButtons
            isLoading={isSaving}
            onCancel={onClose}
            onSubmit={handleSave}
            submitText="Save"
            containerStyle={{ backgroundColor: redesign.card.bg }}
          />
        </View>
      </View>
    </DesktopSidePanel>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  footer: {
    borderTopWidth: 1,
  },
  bodyContent: {
    paddingHorizontal: 18,
    paddingTop: 8,
    gap: 16,
  },
  threadTitle: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 4,
  },
  autoTagNote: {
    fontSize: 12,
    fontStyle: 'italic',
  },
});
